import React, { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { Text, Space } from '@redlink/components/atoms';

export const Button = memo(
  ({
    title,
    style,
    activeOpacity,
    variant = 'secondary',
    icon,
    textStyle,
    children,
    disabled,
    borderRadius,
    textContainerStyle,
    ...rest
  }) => {
    const { styles } = useStyle();
    const hasIcon = !!icon;
    const hasTitle = !!title;
    const hasChildren = !!children;
    return (
      <TouchableOpacity
        {...rest}
        {...{ disabled }}
        activeOpacity={0.6}
        style={[
          styles.container,
          styles[`${variant}Container`],
          disabled && { opacity: 0.5 },
          borderRadius && { borderRadius },
          style,
        ]}
      >
        {!hasChildren && (
          <>
            {hasIcon && (
              <>
                {icon}
                {hasTitle && <Space size={8} />}
              </>
            )}
            <View style={[styles.textContainer, textContainerStyle]}>
              <Text
                numberOfLines={1}
                style={[styles.text, styles[`${variant}Text`], textStyle]}
              >
                {title}
              </Text>
            </View>
          </>
        )}
        {hasChildren && children}
      </TouchableOpacity>
    );
  }
);

const useStyle = createUseStyle(({ colors, size, shadow }) => ({
  container: {
    padding: 8,
    alignItems: 'center',
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: 'row',
  },
  primaryContainer: {
    backgroundColor: colors.red1,
    borderColor: colors.red1,
  },
  transparentContainer: {
    borderColor: colors.transparent,
  },
  blueContainer: {
    backgroundColor: colors.blue1,
    borderColor: colors.blue1,
    borderRadius: 24,
  },
  switchContainer: {
    backgroundColor: colors.gray7,
    borderColor: colors.gray7,
    borderRadius: 24,
  },
  switchSelectedContainer: {
    backgroundColor: colors.blue1,
    borderColor: colors.blue1,
    borderRadius: 24,
  },
  blueGlassContainer: {
    backgroundColor: colors.blue1,
    borderColor: colors.blue1,
    borderRadius: 10,
  },
  greenGlassContainer: {
    backgroundColor: colors.green1,
    borderColor: colors.green1,
    borderRadius: 10,
  },
  selectedContainer: {
    backgroundColor: colors.blue2,
    borderColor: colors.blue2,
    borderRadius: 8,
  },
  grayContainer: {
    backgroundColor: colors.gray7,
    borderColor: colors.gray7,
    borderRadius: 5,
  },
  commandContainer: {
    backgroundColor: colors.gray7,
    borderColor: colors.gray7,
    borderRadius: 5,
  },
  linkContainer: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  cardContainer: {
    backgroundColor: colors.blue1,
    borderColor: colors.blue1,
    borderRadius: 10,
  },
  secondaryContainer: {
    backgroundColor: colors.white,
    borderColor: colors.gray22,
  },
  secondarySimpleContainer: {
    backgroundColor: colors.white,
    borderColor: colors.transparent,
  },
  yellowContainer: {
    backgroundColor: colors.yellow1,
    borderColor: colors.yellow1,
    borderRadius: 24,
  },
  greenContainer: {
    backgroundColor: colors.green1,
    borderColor: colors.green1,
  },
  circleContainer: {
    backgroundColor: colors.blue1,
    borderColor: colors.blue1,
    borderRadius: 100,
  },
  circleHoverContainer: {
    backgroundColor: colors.blue4,
    borderColor: colors.transparent,
    borderRadius: 100,
  },
  darkContainer: {
    flexDirection: 'row-reverse',
    marginVertical: 8,
    borderRadius: 5,
    borderColor: colors.gray22,
  },
  actionBlueContainer: {
    backgroundColor: colors.blue1,
    borderColor: colors.blue1,
    borderRadius: 10,
    paddingVertical: 1,
    paddingHorizontal: 14,
  },
  actionBlueIconContainer: {
    backgroundColor: colors.blue1,
    borderWidth: 0,
    borderColor: colors.blue1,
    borderRadius: 3,
    padding: 0,
  },
  text: {
    fontSize: 14,
  },
  primaryText: {
    color: colors.white,
  },
  transparentText: {
    color: colors.gray23,
  },
  blueText: {
    color: colors.white,
  },
  darkText: {
    color: colors.gray21,
  },
  actionBlueText: {
    color: colors.white,
    fontSize: 12,
  },
  switchText: {
    color: colors.gray23,
    marginTop: 3,
  },
  switchSelectedText: {
    color: colors.white,
    marginTop: 3,
  },
  blueGlassText: {
    color: colors.white,
  },
  greenGlassText: {
    color: colors.white,
  },
  selectedText: {
    color: colors.blue1,
    fontWeight: '600',
  },
  yellowText: {
    color: colors.white,
  },
  greenText: {
    color: colors.white,
  },
  linkText: {
    color: colors.blue1,
    textDecorationLine: 'underline',
  },
  grayText: {
    color: colors.gray23,
    fontWeight: '600',
  },
  circleText: {
    color: colors.white,
  },
  circleHoverText: {
    color: colors.blue1,
  },
  commandText: {
    color: colors.gray23,
  },
  secondaryText: {
    color: colors.red1,
  },
  secondarySimpleText: {
    color: colors.red1,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
}));
